/**
 * T-162-002 — Enterprise upgrade modal with ROI calculator (US-162 / F-179).
 *
 * Rendered by the Evidence Safe route guard when the tier gate denies a
 * non-Enterprise account. Shows the feature value proposition, an ROI
 * calculator seeded from the account's compliance incident history (how many
 * evidence-loss incidents per month the account has had), and a CTA to the
 * pricing page with an Enterprise preselect (?plan=enterprise&upgrade=1).
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { EvidenceSafeGateResponse } from './guard';
import MeetingLinkCta from '../../components/Shared/MeetingLinkCta';

/** Conservative default cost of one lost-evidence incident:
 *  staff recovery time + dispute/penalty exposure. Documented, not hidden. */
export const EVIDENCE_LOSS_INCIDENT_COST_USD = 400;
export const EVIDENCE_LOSS_HOURS_PER_INCIDENT = 0.75;
export const EVIDENCE_SAFE_CTA_HREF = '/pricing?plan=enterprise&upgrade=1';

export interface EvidenceRoi {
    incidentsPerMonth: number;
    monthlyAvoidedUsd: number;
    annualAvoidedUsd: number;
    annualHoursRecovered: number;
}

/** Pure ROI projection (exported for tests). Clamps negatives to zero. */
export function calculateEvidenceRoi(
    incidentsPerMonth: number,
    costPerIncident: number = EVIDENCE_LOSS_INCIDENT_COST_USD
): EvidenceRoi {
    const incidents = Number.isFinite(incidentsPerMonth) && incidentsPerMonth > 0 ? incidentsPerMonth : 0;
    const cost = Number.isFinite(costPerIncident) && costPerIncident > 0 ? costPerIncident : EVIDENCE_LOSS_INCIDENT_COST_USD;
    const monthlyAvoidedUsd = incidents * cost;
    return {
        incidentsPerMonth: incidents,
        monthlyAvoidedUsd,
        annualAvoidedUsd: monthlyAvoidedUsd * 12,
        annualHoursRecovered: incidents * EVIDENCE_LOSS_HOURS_PER_INCIDENT * 12,
    };
}

const money = (v: number) =>
    v.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

interface EnterpriseUpgradeModalProps {
    isOpen: boolean;
    onClose: () => void;
    gateResponse?: EvidenceSafeGateResponse;
    /** Account's historical evidence-loss incidents per month (seeds the ROI input). */
    incidentHistory?: number;
}

export const EnterpriseUpgradeModal: React.FC<EnterpriseUpgradeModalProps> = ({
    isOpen,
    onClose,
    gateResponse,
    incidentHistory = 2,
}) => {
    const navigate = useNavigate();
    const [incidents, setIncidents] = useState<number>(incidentHistory);

    if (!isOpen) return null;
    const roi = calculateEvidenceRoi(incidents);

    return (
        <div
            className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4"
            data-testid="evidence-safe-upgrade-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Enterprise upgrade required"
        >
            <div className="bg-white rounded-2xl max-w-lg w-full p-6 md:p-8 shadow-2xl max-h-[90vh] overflow-y-auto">
                <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center">
                        <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                            <i className="fas fa-vault text-indigo-600"></i>
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-gray-900">Evidence Safe is an Enterprise feature</h2>
                            {gateResponse && (
                                <p className="text-xs text-gray-500" data-testid="gate-reason">{gateResponse.reason}</p>
                            )}
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        aria-label="Close upgrade dialog"
                        className="text-gray-400 hover:text-gray-600 text-xl leading-none"
                    >
                        &times;
                    </button>
                </div>

                <ul className="space-y-2 text-sm text-gray-700 mb-5" data-testid="evidence-safe-value-prop">
                    <li className="flex items-start">
                        <i className="fas fa-check text-emerald-500 mr-2 mt-0.5"></i>
                        Tamper-evident evidence chain for deposit, inspection and notice records
                    </li>
                    <li className="flex items-start">
                        <i className="fas fa-check text-emerald-500 mr-2 mt-0.5"></i>
                        Court-ready export bundles with hash-verified provenance
                    </li>
                    <li className="flex items-start">
                        <i className="fas fa-check text-emerald-500 mr-2 mt-0.5"></i>
                        Delivery Proof tracking — prove notices were served, on time
                    </li>
                </ul>

                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 mb-5">
                    <h3 className="text-sm font-bold text-gray-900 mb-1">ROI calculator</h3>
                    <label htmlFor="roi-incidents" className="block text-xs text-gray-600 mb-2">
                        Evidence-loss incidents per month (your account&apos;s compliance incident history)
                    </label>
                    <input
                        id="roi-incidents"
                        type="number"
                        min={0}
                        max={999}
                        value={incidents}
                        data-testid="roi-incidents-input"
                        onChange={(e) => setIncidents(Math.max(0, parseInt(e.target.value || '0', 10) || 0))}
                        className="w-24 border border-gray-300 rounded-lg px-3 py-1.5 text-sm mb-3"
                    />
                    <dl className="grid grid-cols-3 gap-2 text-center">
                        <div>
                            <dt className="text-[11px] text-gray-500">Avoided / month</dt>
                            <dd className="text-sm font-bold text-gray-900" data-testid="roi-monthly">
                                {money(roi.monthlyAvoidedUsd)}
                            </dd>
                        </div>
                        <div>
                            <dt className="text-[11px] text-gray-500">Avoided / year</dt>
                            <dd className="text-sm font-bold text-gray-900" data-testid="roi-annual">
                                {money(roi.annualAvoidedUsd)}
                            </dd>
                        </div>
                        <div>
                            <dt className="text-[11px] text-gray-500">Hours recovered / year</dt>
                            <dd className="text-sm font-bold text-gray-900" data-testid="roi-hours">
                                {roi.annualHoursRecovered}
                            </dd>
                        </div>
                    </dl>
                    <p className="text-[11px] text-gray-400 mt-2">
                        Based on {money(EVIDENCE_LOSS_INCIDENT_COST_USD)} average cost per lost-evidence incident
                        (staff recovery + dispute exposure). Enterprise pricing is scoped to your portfolio.
                    </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                    <a
                        href={EVIDENCE_SAFE_CTA_HREF}
                        data-testid="evidence-safe-cta"
                        onClick={(e) => {
                            e.preventDefault();
                            navigate(EVIDENCE_SAFE_CTA_HREF);
                            onClose();
                        }}
                        className="flex-1 bg-indigo-600 text-white text-center px-5 py-2.5 rounded-lg font-semibold hover:bg-indigo-700 transition-colors"
                    >
                        Upgrade to Enterprise
                    </a>
                    <button
                        onClick={onClose}
                        className="px-5 py-2.5 rounded-lg font-medium text-gray-600 border border-gray-300 hover:bg-gray-50 transition"
                    >
                        Not now
                    </button>
                </div>

                {/* F-188 / T-165-002: meeting-link CTA (upgrade-flow surface) */}
                <div className="text-center mt-4">
                    <MeetingLinkCta
                        tier="enterprise"
                        campaign="evidence-safe-gate"
                        label="Or book a 1:1 with our team"
                        className="text-sm font-medium text-indigo-600 hover:text-indigo-500"
                    />
                </div>
            </div>
        </div>
    );
};

export default EnterpriseUpgradeModal;
